import type { InstrumentMeta } from "./types.ts";

/** Identity of the currently observed MD connection, not a broker login/account identifier. */
export type LastSource = Readonly<{
  epoch: number;
  sessionId: string | null;
  source: string | null;
}>;

export type LastOwner = Readonly<{
  chartId: string;
  selection: number;
  connection: LastSource;
  resolution: number;
  instrument: Readonly<InstrumentMeta> | null;
}>;

/**
 * Best bid / ask of the owned contract as the md stream last sent it (absolute ticks). A side the
 * stream sent with size 0 (or outside safe integers) is unknown (null), never a guessed price.
 */
export type LastQuote = Readonly<{
  bid: number | null;
  bidSize: number | null;
  ask: number | null;
  askSize: number | null;
}>;

export type LastSnapshot = Readonly<{
  owner: LastOwner;
  sequence: number;
  ticks: number | null;
  /** Raw exchange timestamp when supplied; zero is preserved, never an age/ordering rule. */
  tsNs: bigint | null;
  /** Null until a BBO for this owner arrives; cleared with every new owner (selection, source, roll). */
  quote: LastQuote | null;
}>;

const quoteSide = (n: number | null, size: number | null): boolean =>
  (n === null) === (size === null) &&
  (n === null || Number.isSafeInteger(n)) &&
  (size === null || (Number.isSafeInteger(size) && size > 0));

function validQuote(q: LastQuote | null): boolean {
  return q === null || (quoteSide(q.bid, q.bidSize) && quoteSide(q.ask, q.askSize));
}

function sameQuote(a: LastQuote | null, b: LastQuote | null): boolean {
  return a === null || b === null
    ? a === b
    : a.bid === b.bid && a.bidSize === b.bidSize && a.ask === b.ask && a.askSize === b.askSize;
}
export type LastEvent = { kind: "begin"; owner: LastOwner } | { kind: "snapshot"; value: LastSnapshot };

const revision = (n: number): boolean => Number.isSafeInteger(n) && n >= 0;

/** Raw facts only: the existing bar renderer's missing-unit fallback must not authorize Last. */
export function validLastInstrument(m: Readonly<InstrumentMeta>): boolean {
  return (
    m.symbol.length > 0 &&
    Number.isSafeInteger(m.tickNum) &&
    m.tickNum > 0 &&
    Number.isSafeInteger(m.tickDen) &&
    m.tickDen > 0 &&
    Number.isSafeInteger(m.decimals) &&
    m.decimals >= 0
  );
}

function sameInstrument(a: Readonly<InstrumentMeta> | null, b: Readonly<InstrumentMeta> | null): boolean {
  return a === null || b === null
    ? a === b
    : a.symbol === b.symbol &&
        a.exchange === b.exchange &&
        a.tickNum === b.tickNum &&
        a.tickDen === b.tickDen &&
        a.decimals === b.decimals;
}

function sameSource(a: LastSource, b: LastSource): boolean {
  return a.epoch === b.epoch && a.sessionId === b.sessionId && a.source === b.source;
}

function sameOwner(a: LastOwner, b: LastOwner): boolean {
  return (
    a.chartId === b.chartId &&
    a.selection === b.selection &&
    sameSource(a.connection, b.connection) &&
    a.resolution === b.resolution &&
    sameInstrument(a.instrument, b.instrument)
  );
}

/** Inert presentation state shared by worker and compat views; never reads bars or history. */
export class OwnedLast {
  private chartId: string | null = null;
  private selection = 0;
  private current: LastSnapshot | null = null;

  /** Current host selection, including the interval before an owner begin arrives. */
  get selectionRevision(): number {
    return this.selection;
  }

  get snapshot(): LastSnapshot | null {
    return this.current;
  }

  /** Called synchronously by the host on attach/selection, before the worker receives the command. */
  select(chartId: string, selection: number): void {
    if (!revision(selection)) return;
    if (chartId === this.chartId && selection <= this.selection) return;
    this.chartId = chartId;
    this.selection = selection;
    this.current = null;
  }

  begin(owner: LastOwner): boolean {
    if (
      owner.chartId !== this.chartId ||
      owner.selection !== this.selection ||
      !revision(owner.connection.epoch) ||
      !revision(owner.resolution) ||
      (owner.instrument !== null &&
        (!owner.connection.sessionId || !owner.connection.source || !validLastInstrument(owner.instrument)))
    )
      return false;
    const previous = this.current?.owner;
    if (previous) {
      if (owner.connection.epoch < previous.connection.epoch) return false;
      if (
        owner.connection.epoch === previous.connection.epoch &&
        (!sameSource(owner.connection, previous.connection) || owner.resolution <= previous.resolution)
      )
        return false;
    }
    this.current = { owner, sequence: 0, ticks: null, tsNs: null, quote: null };
    return true;
  }

  receive(value: LastSnapshot): boolean {
    if (
      !this.current ||
      this.current.owner.instrument === null ||
      !sameOwner(value.owner, this.current.owner) ||
      !revision(value.sequence) ||
      value.sequence <= this.current.sequence ||
      (value.ticks !== null && !Number.isSafeInteger(value.ticks)) ||
      (value.tsNs !== null && value.tsNs < 0n) ||
      !validQuote(value.quote)
    )
      return false;
    this.current = value;
    return true;
  }
}

/** Worker-side publisher. All emitted values retain the owner from the moment of reception. */
export class LastPublisher {
  private readonly chartId: string;
  private selection: number;
  private readonly emit: (event: LastEvent) => void;
  private connection: LastSource = { epoch: 0, sessionId: null, source: null };
  private resolution = 0;
  private instrument: Readonly<InstrumentMeta> | null = null;
  private owner: LastOwner | null = null;
  private sequence = 0;
  private ticks: number | null = null;
  private tsNs: bigint | null = null;
  private quoteValue: LastQuote | null = null;

  constructor(chartId: string, selection: number, emit: (event: LastEvent) => void) {
    this.chartId = chartId;
    this.selection = selection;
    this.emit = emit;
  }

  select(selection: number): void {
    if (!revision(selection) || selection <= this.selection) return;
    this.selection = selection;
    this.instrument = null;
    this.begin();
  }

  source(connection: LastSource): void {
    if (!revision(connection.epoch) || connection.epoch < this.connection.epoch) return;
    if (this.owner && connection.epoch <= this.connection.epoch) return;
    this.connection = { ...connection };
    this.instrument = null;
    this.begin();
  }

  resolved(meta: InstrumentMeta): void {
    const next =
      this.connection.sessionId && this.connection.source && validLastInstrument(meta) ? { ...meta } : null;
    if (sameInstrument(next, this.instrument)) return;
    this.instrument = next;
    this.begin();
  }

  /** An explicit stream gap invalidates the value without pretending the instrument disappeared. */
  gap(symbol?: string): void {
    if (symbol !== undefined && symbol !== this.instrument?.symbol) return;
    this.begin();
  }

  trade(symbol: string, prices: readonly bigint[], times: readonly bigint[] = []): void {
    if (!this.owner || !this.instrument || symbol !== this.instrument.symbol || prices.length === 0) return;
    const last = prices[prices.length - 1];
    const next =
      last !== undefined && last >= BigInt(Number.MIN_SAFE_INTEGER) && last <= BigInt(Number.MAX_SAFE_INTEGER)
        ? Number(last)
        : null;
    const time = times[prices.length - 1];
    const tsNs = time !== undefined && time >= 0n ? time : null;
    if (next === this.ticks && tsNs === this.tsNs) return;
    this.ticks = next;
    this.tsNs = tsNs;
    this.emit({
      kind: "snapshot",
      value: { owner: this.owner, sequence: ++this.sequence, ticks: next, tsNs, quote: this.quoteValue },
    });
  }

  /**
   * Best bid / ask for the owned contract (md `Bbo`, absolute ticks). Same ownership as `trade`:
   * another symbol, no owner or no accepted instrument is ignored. A side with size 0 is unknown.
   */
  quote(symbol: string, bidTicks: bigint, bidSize: number, askTicks: bigint, askSize: number): void {
    if (!this.owner || !this.instrument || symbol !== this.instrument.symbol) return;
    const side = (ticks: bigint, size: number): [number | null, number | null] =>
      Number.isSafeInteger(size) &&
      size > 0 &&
      ticks >= BigInt(Number.MIN_SAFE_INTEGER) &&
      ticks <= BigInt(Number.MAX_SAFE_INTEGER)
        ? [Number(ticks), size]
        : [null, null];
    const [bid, bs] = side(bidTicks, bidSize);
    const [ask, as] = side(askTicks, askSize);
    const next: LastQuote = { bid, bidSize: bs, ask, askSize: as };
    if (sameQuote(next, this.quoteValue)) return;
    this.quoteValue = next;
    this.emit({
      kind: "snapshot",
      value: {
        owner: this.owner,
        sequence: ++this.sequence,
        ticks: this.ticks,
        tsNs: this.tsNs,
        quote: next,
      },
    });
  }

  private begin(): void {
    this.owner = {
      chartId: this.chartId,
      selection: this.selection,
      connection: this.connection,
      resolution: ++this.resolution,
      instrument: this.instrument,
    };
    this.sequence = 0;
    this.ticks = null;
    this.tsNs = null;
    this.quoteValue = null;
    this.emit({ kind: "begin", owner: this.owner });
  }
}
